import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Sparkles, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Working languages switch the UI; LT/EE are not translated yet and are offered
// as sponsorable "invest" targets instead.
const FLAGS = [
  { code: 'lv', flag: '🇱🇻', label: 'Latviski', kind: 'lang' },
  { code: 'lt', flag: '🇱🇹', label: 'Lietuvių', kind: 'invest', campaign: 'lang_lt', name: 'Lietuviešu' },
  { code: 'ee', flag: '🇪🇪', label: 'Eesti', kind: 'invest', campaign: 'lang_ee', name: 'Igauņu' },
  { code: 'en', flag: '🇬🇧', label: 'English', kind: 'lang' },
  { code: 'ru', flag: '🇷🇺', label: 'Русский', kind: 'lang' },
];

export const InvestLanguageFlags = ({ className = '' }) => {
  const { language, setLanguage } = useLanguage();
  const [target, setTarget] = useState(null); // { campaign, name }

  return (
    <>
      <div className={`flex flex-wrap items-center justify-center gap-1.5 ${className}`} data-testid="language-flags">
        {FLAGS.map((f) => {
          const active = f.kind === 'lang' && language === f.code;
          const isInvest = f.kind === 'invest';
          return (
            <button
              key={f.code}
              type="button"
              onClick={() => (f.kind === 'lang' ? setLanguage(f.code) : setTarget({ campaign: f.campaign, name: f.name }))}
              title={isInvest ? `Šī valoda vēl nav pieejama — atbalsti ${f.name} tulkojumu` : f.label}
              aria-current={active ? 'true' : undefined}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                active
                  ? 'bg-amber-500 text-[#0A0D0B] shadow-[0_2px_12px_rgba(255,159,28,0.45)] ring-2 ring-amber-300/70'
                  : isInvest
                    ? 'text-white/35 bg-transparent border border-dashed border-white/15 hover:text-amber-200/80 hover:border-amber-400/40 grayscale hover:grayscale-0'
                    : 'text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10'
              }`}
              data-testid={`flag-${f.code}${active ? '-active' : ''}${isInvest ? '-invest' : ''}`}
            >
              <span className={`text-sm leading-none ${isInvest ? 'opacity-60' : ''}`}>{f.flag}</span>
              <span>{f.label}</span>
              {isInvest && <span className="text-[9px] font-bold uppercase tracking-wide text-amber-400/70">€</span>}
            </button>
          );
        })}
      </div>

      <InvestModal target={target} onClose={() => setTarget(null)} />
    </>
  );
};

export const InvestModal = ({ target, onClose, fixedCampaign, fixedName }) => {
  // Supports both the flag flow (target={campaign,name}) and a direct fixed
  // campaign (e.g. map region button).
  const campaign = target?.campaign || fixedCampaign;
  const name = target?.name || fixedName;
  const isRegion = campaign?.startsWith('region_');
  const isGeneral = campaign === 'general';
  const presets = isRegion ? [500, 1000, 2500, 5000] : isGeneral ? [10, 25, 50, 100] : [25, 50, 100, 250];
  const defaultAmount = isRegion ? 1000 : isGeneral ? 20 : 25;

  const { user } = useAuth();
  const { language } = useLanguage();
  const L = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? (ru ?? en) : en);
  const navigate = useNavigate();
  const [amount, setAmount] = useState(defaultAmount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [investorType, setInvestorType] = useState('private'); // private | company
  const [investorCompany, setInvestorCompany] = useState({ legal_name: '', registration_number: '', vat_number: '' });
  const [agreed, setAgreed] = useState(false);

  const open = !!campaign;

  // Reset to the sensible default whenever a new campaign opens.
  useEffect(() => {
    if (campaign) { setAmount(defaultAmount); setError(''); setInvestorType('private'); setAgreed(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  const invest = async () => {
    setError('');
    if (!user) { navigate('/login'); return; }
    if (!agreed) { setError(L('Lūdzu, piekrīti investīciju noteikumiem', 'Please accept the investment terms', 'Пожалуйста, примите условия инвестиций')); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/investments/checkout`, {
        campaign_key: campaign,
        amount: Number(amount),
        origin_url: window.location.origin,
        investor_type: investorType,
        investor_company: investorType === 'company' ? investorCompany : null,
        terms_accepted: true,
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || L('Neizdevās sākt maksājumu', 'Failed to start payment', 'Не удалось начать оплату'));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="bg-[#0F1310] border-white/10 text-white max-w-md" data-testid="invest-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-2xl" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <Sparkles className="w-5 h-5 text-amber-400" />
            {isRegion
              ? L(`Atbalsti valsti: ${name}`, `Support country: ${name}`, `Поддержать страну: ${name}`)
              : isGeneral
              ? L('Atbalsti KoLV attīstību', 'Support KoLV development', 'Поддержать развитие KoLV')
              : L(`Atbalsti ${name} valodu`, `Support the ${name} language`, `Поддержать язык: ${name}`)}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-sm">
            {L('Kļūsti par KoLV investoru un saņem daļu no peļņas.',
               'Become a KoLV investor and receive a share of the profit.',
               'Станьте инвестором KoLV и получайте долю прибыли.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-2xl bg-amber-500/10 border border-amber-500/25 p-4 text-sm text-amber-100/90 flex gap-3">
            <TrendingUp className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p>
              {L(
                <>Kļūsti par <strong>KoLV investoru</strong>. <strong>10% no KoLV peļņas</strong> tiek sadalīti proporcionāli starp visiem investoriem — jo lielāks tavs ieguldījums, jo lielāka daļa.</>,
                <>Become a <strong>KoLV investor</strong>. <strong>10% of KoLV profit</strong> is shared proportionally among all investors — the larger your contribution, the bigger your share.</>,
                <>Станьте <strong>инвестором KoLV</strong>. <strong>10% прибыли KoLV</strong> распределяются пропорционально между всеми инвесторами — чем больше ваш вклад, тем больше доля.</>)}
            </p>
          </div>

          <div>
            <label className="text-sm text-white/60 mb-2 block">
              {L('Ieguldījuma summa (EUR) — investē cik vēlies',
                 'Investment amount (EUR) — invest as much as you like',
                 'Сумма вклада (EUR) — вкладывайте сколько хотите')}
            </label>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {presets.map((v) => (
                <button key={v} type="button" onClick={() => setAmount(v)}
                  className={`py-2 rounded-xl text-sm font-semibold border transition-colors ${
                    Number(amount) === v ? 'bg-amber-500 text-[#0A0D0B] border-amber-500' : 'bg-white/5 text-white/80 border-white/10 hover:bg-white/10'
                  }`} data-testid={`invest-preset-${v}`}>€{v}</button>
              ))}
            </div>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50">€</span>
              <input
                type="number" min={5} max={10000} value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full h-12 pl-8 pr-4 rounded-2xl bg-white/5 border border-white/10 text-white text-lg font-semibold focus:border-amber-500/60 focus:outline-none"
                data-testid="invest-amount-input"
              />
            </div>
          </div>

          {error && <p className="text-red-300 text-sm" data-testid="invest-error">{error}</p>}

          {/* Investor identity: private person or company */}
          <div data-testid="investor-type">
            <label className="text-sm text-white/60 mb-2 block">{L('Investēju kā', 'I invest as', 'Инвестирую как')}</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { k: 'private', lv: 'Privātpersona', en: 'Private person', ru: 'Частное лицо' },
                { k: 'company', lv: 'Uzņēmums', en: 'Company', ru: 'Компания' },
              ].map((o) => (
                <button key={o.k} type="button" onClick={() => setInvestorType(o.k)} data-testid={`investor-type-${o.k}`}
                  className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${investorType === o.k ? 'bg-amber-500 text-[#0A0D0B] border-amber-500' : 'bg-white/5 text-white/80 border-white/10 hover:bg-white/10'}`}>
                  {L(o.lv, o.en, o.ru)}
                </button>
              ))}
            </div>
            {investorType === 'company' && (
              <div className="mt-3 space-y-2" data-testid="investor-company-fields">
                <input value={investorCompany.legal_name} onChange={(e) => setInvestorCompany({ ...investorCompany, legal_name: e.target.value })} placeholder={L('Uzņēmuma nosaukums', 'Company name', 'Название компании')} className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:border-amber-500/60 focus:outline-none" data-testid="investor-company-name" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={investorCompany.registration_number} onChange={(e) => setInvestorCompany({ ...investorCompany, registration_number: e.target.value })} placeholder={L('Reģ. Nr.', 'Reg. No.', 'Рег. №')} className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:border-amber-500/60 focus:outline-none" data-testid="investor-company-regnr" />
                  <input value={investorCompany.vat_number} onChange={(e) => setInvestorCompany({ ...investorCompany, vat_number: e.target.value })} placeholder={L('PVN Nr.', 'VAT No.', 'НДС №')} className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:border-amber-500/60 focus:outline-none" data-testid="investor-company-vat" />
                </div>
              </div>
            )}
          </div>

          {/* Latvian tax info — differs for private persons vs companies */}
          <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 text-xs text-white/60 leading-relaxed" data-testid="invest-tax-info">
            <p className="font-semibold text-white/80 mb-1">{L('Nodokļu informācija (Latvija)', 'Tax information (Latvia)', 'Налоговая информация (Латвия)')}</p>
            {investorType === 'company' ? (
              <p>{L(
                'Uzņēmumam: peļņas sadale ienāk uzņēmuma ieņēmumos. Uzņēmumu ienākuma nodoklis Latvijā tiek piemērots peļņas sadales brīdī (20/80 režīms). Iekļauj to savā grāmatvedībā. Šī nav nodokļu konsultācija.',
                'For companies: profit distributions count as company income. Latvian corporate income tax applies upon profit distribution (20/80 regime). Record it in your accounting. This is not tax advice.',
                'Для компаний: распределение прибыли — доход компании. Налог на прибыль в Латвии применяется при распределении (режим 20/80). Учитывайте в бухгалтерии. Это не налоговая консультация.')}</p>
            ) : (
              <p>{L(
                'Privātpersonai: saņemtā peļņa ir kapitāla ienākums, kam Latvijā piemēro iedzīvotāju ienākuma nodokli (kapitāla ienākums 20%). Par to jādeklarē VID. Šī nav nodokļu konsultācija.',
                'For private persons: profit received is capital income, subject to Latvian personal income tax (capital income 20%). It must be declared to the SRS (VID). This is not tax advice.',
                'Для частных лиц: полученная прибыль — доход от капитала, облагается подоходным налогом в Латвии (20%). Подлежит декларированию в СГД (VID). Это не налоговая консультация.')}</p>
            )}
          </div>

          {!user && (
            <p className="text-white/50 text-xs">
              {L('Lai investētu, vispirms jāpieslēdzas savam kontam.',
                 'To invest, please sign in to your account first.',
                 'Чтобы инвестировать, сначала войдите в свой аккаунт.')}
            </p>
          )}

          {/* Mandatory terms consent — acceptance date is recorded server-side on purchase */}
          <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="invest-terms-consent">
            <input
              type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-amber-500 shrink-0" data-testid="invest-terms-checkbox"
            />
            <span className="text-xs text-white/70 leading-relaxed">
              {L('Esmu iepazinies un piekrītu KoLV ', 'I have read and accept the KoLV ', 'Я ознакомился и принимаю ')}
              <Link to="/investiciju-noteikumi" target="_blank" rel="noopener noreferrer" className="text-amber-400 underline hover:text-amber-300" data-testid="invest-terms-modal-link">
                {L('Investīciju un peļņas sadales noteikumiem', 'Investment & profit distribution terms', 'условия инвестиций и распределения прибыли KoLV')}
              </Link>.
            </span>
          </label>

          <button
            onClick={invest}
            disabled={loading || (!!user && !agreed)}
            className="w-full h-12 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}
            data-testid="invest-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" />
              : !user ? L('Pieslēgties, lai investētu', 'Sign in to invest', 'Войти, чтобы инвестировать')
              : `${L('Investēt', 'Invest', 'Инвестировать')} €${amount || 0}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
